// sketchProfiles — поиск замкнутых контуров (профилей) в эскизе.
//
// Профиль = замкнутая фигура с площадью, которую можно использовать как
// основу для extrude/revolve.
//
// Виды профилей:
//   1. Окружность — каждая окружность сама по себе профиль.
//   2. Полигон из линий — замкнутая последовательность линий, образующая
//      цикл по точкам. Например, прямоугольник = 4 линии + 4 точки в петле.
//
// Алгоритм для полигонов: строим неориентированный граф (вершины=точки,
// рёбра=линии), ищем минимальные циклы через DFS.
//
// НОВОЕ (Fusion-паритет): основной источник профилей — планарное разбиение
// эскиза (sketchArrangement.findRegions). Оно находит ВСЕ грани, включая
// сектора и дольки, образованные ПЕРЕСЕЧЕНИЕМ кривых (спица × обод, круг ×
// спица), которые граф общих точек не видел. Старый findProfiles оставлен как
// fallback (одиночные круги без пересечений и т.п.).

import { findRegions, pointInRegion, ARC_SEGMENTS } from './sketchArrangement';

/**
 * Найти все замкнутые контуры (профили) в эскизе.
 *
 * Входные данные:
 *   sketch.primitives — массив { kind: 'point'|'line'|'circle', ... }
 *
 * Возвращает массив профилей:
 *   [
 *     { id: 'profile_<i>', kind: 'circle', circleId },
 *     { id: 'profile_<j>', kind: 'polygon', pointIds: [...], lineIds: [...] },
 *   ]
 *
 * id — стабильный, не меняется между вызовами для того же набора рёбер.
 */
export function findProfiles(sketch) {
    if (!sketch || !sketch.primitives) return [];

    // === Основной путь: планарное разбиение (все грани, включая сектора). ===
    // Находит области между пересекающимися кривыми — то, что старый граф не
    // видел. Каждый регион = замкнутый полигон-контур (kind:'region').
    const regions = findRegions(sketch);
    if (regions.length > 0) {
        return regions.map((rg) => ({
            id: rg.id,
            kind: 'region',
            verts: rg.verts,      // [{x,y}...] замкнутый контур
            area: rg.area,
            center: rg.center,
        }));
    }

    // === Fallback (старый алгоритм) — если разбиение ничего не дало. ===
    const profiles = [];

    // 1. Окружности
    for (const p of sketch.primitives) {
        if (p.kind === 'circle') {
            profiles.push({
                id: `profile_circle_${p.id}`,
                kind: 'circle',
                circleId: p.id,
                cx: p.cx,
                cy: p.cy,
                r: p.r,
            });
        }
    }

    // 2. Полигоны из рёбер (линий и дуг).
    // Дуга, как и линия, соединяет две точки (p1/p2) — поэтому участвует
    // в графе контуров наравне с линией. Ребро графа несёт edgeId и
    // edgeKind ('line' | 'arc').
    const points = sketch.primitives.filter((p) => p.kind === 'point');
    /*
     * Вспомогательные линии (construction) в контур НЕ входят.
     *
     * Их рисуют как опору для построений — ось симметрии, линия центров,
     * направляющая под углом. Если пустить их в поиск профилей, они замкнут
     * лишние области, и выдавливание захватит куски, которых человек не
     * рисовал.
     *
     * В решателе они при этом участвуют полноценно: к ним привязываются
     * ограничениями, ради этого их и чертят.
     */
    const lines = sketch.primitives.filter(
        (p) => p.kind === 'line' && !p.construction);
    const arcs = sketch.primitives.filter((p) => p.kind === 'arc');
    const pointById = new Map(points.map((p) => [p.id, p]));

    // Граф: pointId → массив { neighborId, edgeId, edgeKind }
    const graph = new Map();
    for (const p of points) graph.set(p.id, []);
    const addEdge = (e, kind) => {
        if (!pointById.has(e.p1) || !pointById.has(e.p2)) return;
        graph.get(e.p1).push({ neighborId: e.p2, edgeId: e.id, edgeKind: kind });
        graph.get(e.p2).push({ neighborId: e.p1, edgeId: e.id, edgeKind: kind });
    };
    for (const l of lines) addEdge(l, 'line');
    for (const a of arcs) addEdge(a, 'arc');
    // Все рёбра вместе — по ним перебираем циклы.
    const edges = [
        ...lines.map((l) => ({ ...l, edgeKind: 'line' })),
        ...arcs.map((a) => ({ ...a, edgeKind: 'arc' })),
    ];

    // Поиск циклов: для каждого ребра (e.p1 → e.p2) ищем кратчайший путь
    // от p2 до p1 НЕ используя само это ребро. Если нашёлся — есть цикл.
    // Чтобы не дублировать одни и те же циклы, нормализуем по
    // отсортированному списку edgeId.
    const seenCycles = new Set();

    for (const e of edges) {
        const cyclePath = findShortestCycleThroughEdge(graph, e);
        if (!cyclePath) continue;
        const allEdgeIds = cyclePath.edges.map((x) => x.edgeId);
        const sig = [...allEdgeIds].sort().join(',');
        if (seenCycles.has(sig)) continue;
        seenCycles.add(sig);

        // Раскладываем рёбра контура по типам.
        const lineIds = cyclePath.edges
            .filter((x) => x.edgeKind === 'line').map((x) => x.edgeId);
        const arcIds = cyclePath.edges
            .filter((x) => x.edgeKind === 'arc').map((x) => x.edgeId);

        profiles.push({
            id: `profile_poly_${sig}`,
            kind: 'polygon',
            pointIds: cyclePath.pointIds,
            lineIds,                 // совместимость со старым кодом
            arcIds,                  // дуги контура
            edges: cyclePath.edges,  // полный список рёбер контура
        });
    }

    return profiles;
}


/**
 * BFS от e.p2 до e.p1, исключая ребро `e`. Возвращает путь как
 * { pointIds: [...], edges: [{edgeId, edgeKind}, ...] } или null.
 */
function findShortestCycleThroughEdge(graph, e) {
    const start = e.p2;
    const target = e.p1;
    const excludeEdgeId = e.id;

    const visited = new Map(); // pointId → { prev, edgeId, edgeKind }
    visited.set(start, { prev: null, edgeId: null, edgeKind: null });
    const queue = [start];

    while (queue.length > 0) {
        const cur = queue.shift();
        const neighbors = graph.get(cur) || [];
        for (const { neighborId, edgeId, edgeKind } of neighbors) {
            if (edgeId === excludeEdgeId) continue;
            if (visited.has(neighborId)) continue;
            visited.set(neighborId, { prev: cur, edgeId, edgeKind });
            if (neighborId === target) {
                // Восстанавливаем путь
                const pointIds = [];
                const cycleEdges = [];
                let p = neighborId;
                while (p !== null) {
                    pointIds.unshift(p);
                    const v = visited.get(p);
                    if (v.edgeId) {
                        cycleEdges.unshift({
                            edgeId: v.edgeId, edgeKind: v.edgeKind,
                        });
                    }
                    p = v.prev;
                }
                // Замыкаем цикл — добавляем стартовое ребро
                cycleEdges.push({
                    edgeId: excludeEdgeId, edgeKind: e.edgeKind || 'line',
                });
                return { pointIds, edges: cycleEdges };
            }
            queue.push(neighborId);
        }
    }
    return null;
}


/**
 * Получить вершины полигона как [{x,y}, ...] в порядке обхода.
 *
 * Контур может содержать дуги. Дуга между двумя точками заменяется
 * цепочкой промежуточных точек вдоль дуги (тесселяция) — иначе при
 * выдавливании дуга «срезалась» бы в прямую хорду.
 */
/**
 * Незамкнутые цепочки линий эскиза — основа для рёбер и стенок (Rib/Web).
 *
 * В отличие от findProfiles, который ищет ЗАМКНУТЫЕ контуры под выдавливание,
 * здесь нужны именно открытые пути: ребро жёсткости рисуют одной чертой
 * поперёк корпуса, а не обводят прямоугольником. Так же устроено в Fusion.
 *
 * Возвращает массив { id, kind:'openPath', pointIds:[...], edgeIds:[...] }.
 * Точки идут по порядку — от одного свободного конца к другому.
 */
export function findOpenChains(sketch) {
    if (!sketch || !sketch.primitives) return [];
    const points = sketch.primitives.filter((p) => p.kind === 'point');
    /*
     * Вспомогательные линии (construction) в контур НЕ входят.
     *
     * Их рисуют как опору для построений — ось симметрии, линия центров,
     * направляющая под углом. Если пустить их в поиск профилей, они замкнут
     * лишние области, и выдавливание захватит куски, которых человек не
     * рисовал.
     *
     * В решателе они при этом участвуют полноценно: к ним привязываются
     * ограничениями, ради этого их и чертят.
     */
    const lines = sketch.primitives.filter(
        (p) => p.kind === 'line' && !p.construction);
    const arcs = sketch.primitives.filter((p) => p.kind === 'arc');
    const pointById = new Map(points.map((p) => [p.id, p]));

    const graph = new Map();
    for (const p of points) graph.set(p.id, []);
    const addEdge = (e, kind) => {
        if (!pointById.has(e.p1) || !pointById.has(e.p2)) return;
        graph.get(e.p1).push({ neighborId: e.p2, edgeId: e.id, edgeKind: kind });
        graph.get(e.p2).push({ neighborId: e.p1, edgeId: e.id, edgeKind: kind });
    };
    for (const l of lines) addEdge(l, 'line');
    for (const a of arcs) addEdge(a, 'arc');

    /*
     * Свободный конец — точка ровно с одним соседом. Замкнутый контур таких
     * не имеет вовсе, поэтому он сюда и не попадёт: ровно то, что нужно.
     */
    const ends = [...graph.entries()]
        .filter(([, nb]) => nb.length === 1)
        .map(([id]) => id);

    const chains = [];
    const usedEdges = new Set();

    for (const start of ends) {
        // Цепочку от этого конца уже прошли с другой стороны — пропускаем.
        const first = graph.get(start)[0];
        if (!first || usedEdges.has(first.edgeId)) continue;

        const pointIds = [start];
        const edgeIds = [];
        let prev = start;
        let cur = first;
        // Идём по цепочке, пока путь не ветвится и не кончился.
        while (cur && !usedEdges.has(cur.edgeId)) {
            usedEdges.add(cur.edgeId);
            edgeIds.push(cur.edgeId);
            pointIds.push(cur.neighborId);
            const nb = graph.get(cur.neighborId) || [];
            // Развилка — дальше не идём: какую ветку выбрать, непонятно,
            // а угадывать за человека тут нельзя.
            if (nb.length !== 2) break;
            const next = nb.find((x) => x.neighborId !== prev);
            prev = cur.neighborId;
            cur = next;
        }
        if (edgeIds.length > 0) {
            chains.push({
                id: `openpath_${edgeIds.slice().sort().join('_')}`,
                kind: 'openPath',
                pointIds,
                edgeIds,
            });
        }
    }
    return chains;
}


export function profilePolygonVertices(profile, sketch) {
    // Регион уже несёт готовый контур вершин.
    if (profile.kind === 'region') return profile.verts || [];
    if (profile.kind !== 'polygon') return [];
    const prims = sketch.primitives || [];
    const pointById = new Map(
        prims.filter((p) => p.kind === 'point').map((p) => [p.id, p])
    );
    const arcById = new Map(
        prims.filter((p) => p.kind === 'arc').map((p) => [p.id, p])
    );

    // Старый формат профиля (без edges) — простой обход точек.
    if (!profile.edges || profile.edges.length === 0) {
        return profile.pointIds.map((id) => {
            const p = pointById.get(id);
            return p ? { x: p.x, y: p.y } : null;
        }).filter(Boolean);
    }

    // Идём по точкам контура; для каждого ребра берём ИМЕННО то ребро
    // цикла (profile.edges[i]) — по его edgeId. Раньше дуга искалась
    // «любая между точками cur/next», и для контура «дуга + линия между
    // теми же 2 точками» дуга подхватывалась на ОБОИХ рёбрах → контур
    // вырождался в дугу туда-обратно (нулевая площадь, выдавливалась
    // лента вместо тела).
    const verts = [];
    const pids = profile.pointIds;
    const edgesList = profile.edges || [];
    for (let i = 0; i < pids.length; i++) {
        const cur = pointById.get(pids[i]);
        if (!cur) continue;
        verts.push({ x: cur.x, y: cur.y });
        const next = pointById.get(pids[(i + 1) % pids.length]);
        if (!next) continue;
        // Ребро между pids[i] и pids[i+1] = edgesList[i].
        const edge = edgesList[i];
        if (edge && edge.edgeKind === 'arc') {
            const arc = arcById.get(edge.edgeId);
            if (arc) {
                // fromIsP1 — обходим дугу в её «родном» направлении p1→p2.
                const fromIsP1 = (arc.p1 === cur.id);
                verts.push(...tessellateArc(arc, cur, next, fromIsP1));
            }
        }
        // edgeKind === 'line' — прямой переход, промежуточных нет.
    }
    return verts;
}

// Промежуточные точки вдоль дуги (без концевых — они уже в контуре).
//
// Дуга хранит ccw — направление обхода p1→p2 (против часовой).
// Контур может проходить дугу как p1→p2, так и p2→p1 — учитываем
// через fromIsP1. Без направления тесселяция «срезала» бы дугу в
// кратчайшую сторону и контур с мажорной дугой (>180°) вырождался.
function tessellateArc(arc, fromPt, toPt, fromIsP1, segments = 32) {
    const { cx, cy } = arc;
    const r = Math.hypot(fromPt.x - cx, fromPt.y - cy);
    const a0 = Math.atan2(fromPt.y - cy, fromPt.x - cx);
    const a1 = Math.atan2(toPt.y - cy, toPt.x - cx);

    // Направление обхода именно этого прохода:
    //   ccw задан для p1→p2; если идём p2→p1 — направление инвертируется.
    // Старые дуги без поля ccw — fallback на кратчайшую дугу.
    let ccw;
    if (typeof arc.ccw === 'boolean') {
        ccw = fromIsP1 ? arc.ccw : !arc.ccw;
    } else {
        let s = a1 - a0;
        while (s <= -Math.PI) s += Math.PI * 2;
        while (s > Math.PI) s += -Math.PI * 2;
        ccw = s >= 0;
    }

    // span — угловой размах в нужную сторону, всегда в (0, 2π].
    let span = a1 - a0;
    if (ccw) {
        while (span <= 0) span += Math.PI * 2;
        while (span > Math.PI * 2) span -= Math.PI * 2;
    } else {
        while (span >= 0) span -= Math.PI * 2;
        while (span < -Math.PI * 2) span += Math.PI * 2;
    }

    const out = [];
    for (let i = 1; i < segments; i++) {
        const t = a0 + span * (i / segments);
        out.push({ x: cx + Math.cos(t) * r, y: cy + Math.sin(t) * r });
    }
    return out;
}


/**
 * Проверить попадает ли точка (x,y) в профиль (для hit-test).
 * Для polygon — алгоритм ray casting, для circle — расстояние до центра.
 */
export function pointInProfile(profile, x, y, sketch) {
    if (profile.kind === 'region') {
        return pointInRegion(profile, x, y);
    }
    if (profile.kind === 'circle') {
        const dx = x - profile.cx, dy = y - profile.cy;
        return dx * dx + dy * dy <= profile.r * profile.r;
    }
    if (profile.kind === 'polygon') {
        const verts = profilePolygonVertices(profile, sketch);
        return pointInPolygon(x, y, verts);
    }
    return false;
}


// Ray casting: луч из (x,y) вправо считает пересечения со сторонами полигона
function pointInPolygon(x, y, verts) {
    if (verts.length < 3) return false;
    let inside = false;
    for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
        const xi = verts[i].x, yi = verts[i].y;
        const xj = verts[j].x, yj = verts[j].y;
        const intersect = (yi > y) !== (yj > y) &&
            (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
}


/**
 * Площадь профиля (абсолютная, в мм²). Для окружности — πr²,
 * для полигона — по формуле шнурков (Гаусса).
 */
export function profileArea(profile, sketch) {
    if (profile.kind === 'region') {
        if (typeof profile.area === 'number') return profile.area;
        const verts = profile.verts || [];
        if (verts.length < 3) return 0;
        let a = 0;
        for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
            a += (verts[j].x + verts[i].x) * (verts[j].y - verts[i].y);
        }
        return Math.abs(a) / 2;
    }
    if (profile.kind === 'circle') {
        return Math.PI * profile.r * profile.r;
    }
    if (profile.kind === 'polygon') {
        const verts = profilePolygonVertices(profile, sketch);
        if (verts.length < 3) return 0;
        let a = 0;
        for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
            a += (verts[j].x + verts[i].x) * (verts[j].y - verts[i].y);
        }
        return Math.abs(a) / 2;
    }
    return 0;
}


/**
 * Разложить плоский список профилей на «внешний контур + его дырки».
 *
 * Профиль B считается дыркой профиля A, если центр B лежит внутри A и
 * площадь B меньше A. Для B берётся САМЫЙ МАЛЫЙ из объемлющих A —
 * тогда при вложенности «квадрат → дыра → островок» островок станет
 * отдельным внешним профилем, а не дыркой квадрата.
 *
 * Возвращает массив «составных» профилей:
 *   [{ outer: <profile>, holes: [<profile>, ...] }, ...]
 * Дырки НЕ попадают в список как самостоятельные внешние профили.
 */
export function resolveProfilesWithHoles(profiles, sketch) {
    const meta = profiles.map((p) => ({
        profile: p,
        area: profileArea(p, sketch),
        center: profileCenter(p, sketch),
    }));

    // Для каждого профиля найдём его «родителя» — наименьший по площади
    // профиль, который его содержит.
    const parentOf = new Array(meta.length).fill(-1);
    for (let i = 0; i < meta.length; i++) {
        let best = -1, bestArea = Infinity;
        for (let j = 0; j < meta.length; j++) {
            if (i === j) continue;
            if (meta[j].area <= meta[i].area) continue; // родитель должен быть больше
            if (!pointInProfile(meta[j].profile, meta[i].center.x, meta[i].center.y, sketch)) continue;
            if (meta[j].area < bestArea) { bestArea = meta[j].area; best = j; }
        }
        parentOf[i] = best;
    }

    // Глубина вложенности: 0 = внешний контур, 1 = дырка, 2 = островок в дырке...
    const depth = (i) => {
        let d = 0, cur = i;
        while (parentOf[cur] !== -1) { d++; cur = parentOf[cur]; }
        return d;
    };

    const result = [];
    for (let i = 0; i < meta.length; i++) {
        if (depth(i) % 2 !== 0) continue; // нечётная глубина — это дырка, пропускаем
        const holes = [];
        for (let j = 0; j < meta.length; j++) {
            // прямые дети чётного профиля = его дырки
            if (parentOf[j] === i) holes.push(meta[j].profile);
        }
        result.push({ outer: meta[i].profile, holes });
    }
    return result;
}


/**
 * Собрать один контур как payload для воркера:
 *   { kind:'circle', cx, cy, r } либо { kind:'polygon', verts:[...] }
 * Возвращает null, если полигон не замкнут (<3 вершин).
 */
/*
 * Совпадает ли контур региона с ЦЕЛОЙ окружностью эскиза?
 *
 * Планарное разбиение отдаёт любой профиль ломаной, поэтому круг, не
 * разрезанный другими кривыми, приезжает сюда как 64-звенный полигон. Если
 * узнать в нём исходную окружность, можно отдать ядру точную дугу вместо
 * ломаной — тогда границы соседних тел совпадают идеально и при объединении
 * не остаётся щели.
 *
 * Проверка строгая: все вершины на одном расстоянии от центра окружности
 * (допуск 1e-6 мм — на порядки меньше любого станочного) и их столько же,
 * сколько даёт тесселяция. Фрагмент круга под условие не подходит и остаётся
 * полигоном — точного представления одной окружностью у него нет.
 */
function regionAsCircle(verts, sketch) {
    const prims = (sketch && sketch.primitives) || [];
    const EPS = 1e-6;
    for (const p of prims) {
        if (p.kind !== 'circle') continue;
        let all = true;
        for (const v of verts) {
            const d = Math.hypot(v.x - p.cx, v.y - p.cy);
            if (Math.abs(d - p.r) > EPS) { all = false; break; }
        }
        // Все вершины на окружности — но это ещё может быть её дуга.
        // Целый круг замкнут, поэтому вершин столько же, сколько в тесселяции.
        if (all && verts.length >= ARC_SEGMENTS) {
            return { kind: 'circle', cx: p.cx, cy: p.cy, r: p.r };
        }
    }
    return null;
}


/*
 * Помещается ли профиль inner внутрь outer целиком?
 *
 * Сначала дешёвая отсечка по габаритам, затем проверка каждой вершины.
 * Вершин у профиля десятки — это заметно дешевле, чем булева операция,
 * на которой ядро потом упало бы.
 */
function profileFitsInside(inner, outer, sketch) {
    const iv = profilePolygonVertices(inner, sketch);
    if (!iv.length) return false;

    const ov = profilePolygonVertices(outer, sketch);
    if (ov.length) {
        let iMinX = Infinity, iMaxX = -Infinity, iMinY = Infinity, iMaxY = -Infinity;
        for (const v of iv) {
            if (v.x < iMinX) iMinX = v.x;
            if (v.x > iMaxX) iMaxX = v.x;
            if (v.y < iMinY) iMinY = v.y;
            if (v.y > iMaxY) iMaxY = v.y;
        }
        let oMinX = Infinity, oMaxX = -Infinity, oMinY = Infinity, oMaxY = -Infinity;
        for (const v of ov) {
            if (v.x < oMinX) oMinX = v.x;
            if (v.x > oMaxX) oMaxX = v.x;
            if (v.y < oMinY) oMinY = v.y;
            if (v.y > oMaxY) oMaxY = v.y;
        }
        // Допуск: вершины дырки могут лежать ровно на границе (касание).
        const E = 1e-6;
        if (iMinX < oMinX - E || iMaxX > oMaxX + E
            || iMinY < oMinY - E || iMaxY > oMaxY + E) return false;
    }

    for (const v of iv) {
        if (!pointInProfile(outer, v.x, v.y, sketch)) return false;
    }
    return true;
}


function contourPayload(profile, sketch) {
    if (profile.kind === 'region') {
        const verts = profile.verts || [];
        if (verts.length < 3) return null;
        /* Целый круг отдаём ядру НАСТОЯЩЕЙ окружностью, а не ломаной:
           иначе боковые поверхности соседних тел не совпадают и при
           объединении остаётся видимая щель. */
        const circle = regionAsCircle(verts, sketch);
        if (circle) return circle;
        return { kind: 'polygon', verts };
    }
    if (profile.kind === 'circle') {
        return { kind: 'circle', cx: profile.cx, cy: profile.cy, r: profile.r };
    }
    if (profile.kind === 'polygon') {
        const verts = profilePolygonVertices(profile, sketch);
        if (verts.length < 3) return null;
        return { kind: 'polygon', verts };
    }
    return null;
}


/**
 * Построить payload профиля для extrude/revolve С УЧЁТОМ дырок.
 *
 * Берёт выбранный пользователем внешний профиль `profile`, находит среди
 * `allProfiles` те, что лежат внутри него (дырки), и прикладывает их как
 * profile.holes — тогда воркер выдавит деталь с отверстиями за одну операцию.
 *
 * Возвращает payload вида
 *   { kind:'polygon'|'circle', ..., holes:[{kind,...}, ...] }
 * или null, если внешний контур некорректен.
 */
export function buildProfilePayloadWithHoles(profile, allProfiles, sketch) {
    const outer = contourPayload(profile, sketch);
    if (!outer) return null;

    // Дырки = профили, чей центр внутри выбранного и площадь меньше.
    const outerArea = profileArea(profile, sketch);
    const holes = [];
    for (const other of allProfiles) {
        if (other.id === profile.id) continue;
        const oa = profileArea(other, sketch);
        if (!(oa < outerArea)) continue;
        const c = profileCenter(other, sketch);
        if (!pointInProfile(profile, c.x, c.y, sketch)) continue;
        /*
         * Дырка обязана помещаться во внешнем контуре ЦЕЛИКОМ.
         *
         * Одного центроида мало: у КОЛЬЦА, охватывающего внешний контур
         * снаружи, центроид тоже лежит внутри, а площадь мала — оба прежних
         * условия выполнялись, кольцо вычиталось целиком и профиль
         * схлопывался в пустоту («Trying to sketch an empty drawing»).
         */
        if (!profileFitsInside(other, profile, sketch)) continue;
        // Не берём в дырки то, что на самом деле вложено в другую дырку
        // (островок): у него есть промежуточный родитель меньше outer.
        let nested = false;
        for (const mid of allProfiles) {
            if (mid.id === profile.id || mid.id === other.id) continue;
            const ma = profileArea(mid, sketch);
            if (ma < outerArea && ma > oa &&
                pointInProfile(mid, c.x, c.y, sketch)) { nested = true; break; }
        }
        if (nested) continue;
        const hp = contourPayload(other, sketch);
        if (hp) holes.push(hp);
    }
    if (holes.length) outer.holes = holes;
    return outer;
}


/**
 * Сместить замкнутый полигон на расстояние d (offset).
 * Каждая вершина сдвигается вдоль биссектрисы угла. Знак d: >0 наружу,
 * <0 внутрь (относительно CCW-ориентации). Возвращает массив вершин [{x,y}]
 * или null, если контур вырожденный.
 *
 * Простой вершинный offset: подходит для прямоугольников, многоугольников,
 * умеренно-вогнутых контуров (типовые эскизы школьника). Самопересечения на
 * сильно-вогнутых углах не разрешаются (как и в большинстве простых CAD).
 */
export function offsetPolygonVertices(verts, d) {
    const n = verts.length;
    if (n < 3) return null;
    // Ориентация (знак площади): для CCW area>0.
    let area = 0;
    for (let i = 0, j = n - 1; i < n; j = i++) {
        area += (verts[j].x + verts[i].x) * (verts[j].y - verts[i].y);
    }
    const ccw = area < 0; // (наша Y вниз/вверх — учитываем знаком)
    const sign = ccw ? 1 : -1;
    const out = [];
    for (let i = 0; i < n; i++) {
        const prev = verts[(i - 1 + n) % n];
        const cur = verts[i];
        const next = verts[(i + 1) % n];
        // Единичные нормали к рёбрам (prev→cur) и (cur→next).
        const e1x = cur.x - prev.x, e1y = cur.y - prev.y;
        const e2x = next.x - cur.x, e2y = next.y - cur.y;
        const l1 = Math.hypot(e1x, e1y) || 1;
        const l2 = Math.hypot(e2x, e2y) || 1;
        // Нормаль (поворот ребра на -90°): (ey, -ex)/len.
        const n1x = e1y / l1, n1y = -e1x / l1;
        const n2x = e2y / l2, n2y = -e2x / l2;
        // Биссектриса нормалей.
        let bx = n1x + n2x, by = n1y + n2y;
        const bl = Math.hypot(bx, by);
        if (bl < 1e-6) { bx = n1x; by = n1y; } else { bx /= bl; by /= bl; }
        // Масштаб биссектрисы: 1/cos(полуугол) = 1/(b·n1).
        const cosHalf = Math.max(0.2, bx * n1x + by * n1y); // clamp против шпилей
        const shift = (d * sign) / cosHalf;
        out.push({ x: cur.x + bx * shift, y: cur.y + by * shift });
    }
    return out;
}


/**
 * Центр профиля (для размещения подсказок и preview-стрелок extrude).
 */
export function profileCenter(profile, sketch) {
    if (profile.kind === 'region') {
        if (profile.center) return profile.center;
        const verts = profile.verts || [];
        if (verts.length === 0) return { x: 0, y: 0 };
        let sx = 0, sy = 0;
        for (const v of verts) { sx += v.x; sy += v.y; }
        return { x: sx / verts.length, y: sy / verts.length };
    }
    if (profile.kind === 'circle') {
        return { x: profile.cx, y: profile.cy };
    }
    if (profile.kind === 'polygon') {
        const verts = profilePolygonVertices(profile, sketch);
        if (verts.length === 0) return { x: 0, y: 0 };
        let sx = 0, sy = 0;
        for (const v of verts) { sx += v.x; sy += v.y; }
        return { x: sx / verts.length, y: sy / verts.length };
    }
    return { x: 0, y: 0 };
}
